import { useEffect, useMemo } from 'react';
import { Button, Drawer } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { updateInvestorCustomDetails } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { CustomDetailDataTypeEnum } from '@dealstack/shared/lib/generated';
import { CustomFieldBooleanInput } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/CustomFieldBooleanInput.tsx';
import { InvestorCustomField } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/customFields.utils.ts';
import { CustomFieldsDrawerStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/CustomFieldsDrawer.styles.tsx';
import {
	CustomFieldsShape,
	type CustomFieldsFormValues,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/CustomFieldsDrawer.types.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	investorId: string;
	customFields: InvestorCustomField[];
	onSaved: () => void;
}

export const CustomFieldsDrawer = ({
	opened,
	onClose,
	investorId,
	customFields,
	onSaved,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const saveRequest = useMutation({ mutationFn: updateInvestorCustomDetails });

	const initialValues = useMemo<CustomFieldsFormValues>(
		() => ({
			values: Object.fromEntries(
				customFields.map((customField) => [customField.id, customField.value ?? ''])
			),
		}),
		[customFields]
	);

	const form = useForm<CustomFieldsFormValues>({
		resolver: zodResolver(
			CustomFieldsShape(
				customFields,
				tMipManage('investors.details.custom_fields.invalid_number')
			)
		),
		defaultValues: initialValues,
	});

	useEffect(() => {
		if (opened) {
			form.reset(initialValues);
		}
	}, [opened, initialValues, form]);

	const onSubmit = ({ values }: CustomFieldsFormValues) => {
		saveRequest.mutate(
			{
				investorId,
				payload: {
					values: customFields.map((customField) => ({
						customDetailId: customField.id,
						value: values[customField.id],
					})),
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tMipManage('investors.details.custom_fields.save_success'),
						state: 'success',
					});
					onSaved();
					onClose();
				},
			}
		);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('investors.details.custom_fields.title')}
			size="484px"
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_CUSTOM_FIELDS_DRAWER}
		>
			<FormProvider {...form}>
				<form style={{ height: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
					<CustomFieldsDrawerStyles>
						{customFields.map((customField) =>
							customField.dataType === CustomDetailDataTypeEnum.BOOLEAN ? (
								<CustomFieldBooleanInput
									key={customField.id}
									name={`values.${customField.id}`}
									label={customField.name}
									description={customField.instruction}
								/>
							) : (
								<TextInput
									key={customField.id}
									name={`values.${customField.id}`}
									label={customField.name}
									description={customField.instruction}
									placeholder={customField.name}
									size="md"
								/>
							)
						)}

						<div className="actions">
							<Button
								fullWidth
								type="submit"
								fw={600}
								loading={saveRequest.isPending}
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_CUSTOM_FIELDS_SAVE
								}
							>
								{tMipManage('investors.details.custom_fields.save')}
							</Button>

							<Button
								variant="subtle"
								fullWidth
								c="dark.8"
								fw={600}
								disabled={saveRequest.isPending}
								onClick={onClose}
							>
								{tMipManage('investors.details.custom_fields.cancel')}
							</Button>
						</div>
					</CustomFieldsDrawerStyles>
				</form>
			</FormProvider>
		</Drawer>
	);
};
